import React, { Suspense, Component, ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { AIAgentProvider } from './context/AIAgentContext';
import { PlatformAdminAuthProvider } from './context/PlatformAdminAuthContext';
import RouterListener from './components/util/RouterListener';
import { ScriptureLoader } from './components/common/ScriptureLoader';
import { getContextFromPath } from './utils/routeContext';
import { UpgradeModal } from './components/subscription';

// =============================================================================
// ERROR BOUNDARY - Catches errors in lazy-loaded components
// =============================================================================

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console in development
    console.error('Route Error Boundary caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '20px',
          textAlign: 'center',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          background: 'var(--background-secondary, #f5f5f5)'
        }}>
          <h1 style={{ marginBottom: '16px', color: '#333' }}>Something went wrong</h1>
          <p style={{ marginBottom: '24px', color: 'var(--text-secondary, #666)', maxWidth: '400px' }}>
            We're sorry, but something unexpected happened. Please try refreshing the page.
          </p>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <pre style={{
              marginBottom: '24px',
              padding: '16px',
              background: '#fee',
              border: '1px solid #fcc',
              borderRadius: '8px',
              maxWidth: '600px',
              overflow: 'auto',
              textAlign: 'left',
              fontSize: '12px'
            }}>
              {this.state.error.message}
            </pre>
          )}
          <button
            onClick={this.handleRetry}
            style={{
              padding: '12px 24px',
              background: '#4f46e5',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Eager-loaded pages (small, frequently used)
import HomePage from './pages/HomePage';
import NotFoundPageWrapper from './pages/NotFoundPageWrapper';

// Lazy-loaded pages (heavy, feature-specific)
const ThemeExample = React.lazy(() => import('./components/ThemeExample'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const ExecutiveDashboardPage = React.lazy(() => import('./pages/ExecutiveDashboardPageBasic'));
const MinistryDashboardPage = React.lazy(() => import('./pages/MinistryDashboardPage'));
const PastoralCareDashboardPage = React.lazy(() => import('./pages/PastoralCareDashboardPageBasic'));
const ImpactDashboardPage = React.lazy(() => import('./pages/ImpactDashboardPage'));
const AssessmentListPage = React.lazy(() => import('./pages/AssessmentListPage'));
const AssessmentCreatePageNew = React.lazy(() => import('./pages/AssessmentCreatePageNew'));
const AssessmentEditPage = React.lazy(() => import('./pages/AssessmentEditPage'));
const AssessmentDetailPage = React.lazy(() => import('./pages/AssessmentDetailPage'));
const AssessmentResponsePage = React.lazy(() => import('./pages/AssessmentResponsePage'));
const AssessmentTemplatePage = React.lazy(() => import('./pages/AssessmentTemplatePage'));
const AssessmentTemplateLibraryPage = React.lazy(() => import('./pages/AssessmentTemplateLibraryPage'));
const AssessmentAnalysisPage = React.lazy(() => import('./pages/AssessmentAnalysisPage'));
const AnalyticsPage = React.lazy(() => import('./pages/AnalyticsPage'));
const AnalyticsDashboardPage = React.lazy(() => import('./pages/AnalyticsDashboardPage'));
const ReportingPage = React.lazy(() => import('./pages/ReportingPage'));
const ReportSchedulingPage = React.lazy(() => import('./pages/ReportSchedulingPage'));
const DashboardCustomizationPage = React.lazy(() => import('./pages/DashboardCustomizationPage'));
const ChurchProfilePage = React.lazy(() => import('./pages/ChurchProfilePage'));
const VisualizationCreatePage = React.lazy(() => import('./pages/VisualizationCreatePage'));
const UserProfilePage = React.lazy(() => import('./pages/UserProfilePage'));
const ThemePaletteBuilderPage = React.lazy(() => import('./pages/ThemePaletteBuilder/ThemePaletteBuilderPage'));
const ChartTestPage = React.lazy(() => import('./pages/ChartTestPage'));
const MemberEngagementPage = React.lazy(() => import('./pages/MemberEngagementPage'));
const MinistryDetailPage = React.lazy(() => import('./pages/MinistryDetailPage'));
const SurveyPage = React.lazy(() => import('./pages/SurveyPage').then(m => ({ default: m.SurveyPage })));
const MemberManagementPage = React.lazy(() => import('./pages/MemberManagementPage'));
const MemberProfile = React.lazy(() => import('./components/member/MemberProfile'));
const MemberCreatePage = React.lazy(() => import('./pages/MemberCreatePage'));
const TestSelectPage = React.lazy(() => import('./pages/TestSelectPage'));
const TestSelectDebug = React.lazy(() => import('./pages/TestSelectDebug'));
const SelectDiagnosticPage = React.lazy(() => import('./pages/SelectDiagnosticPage'));
const KnowledgeBasePage = React.lazy(() => import('./pages/KnowledgeBasePage'));
const StrategicPlanningPage = React.lazy(() => import('./pages/StrategicPlanningPage'));
const LearningPathsMainPage = React.lazy(() => import('./pages/LearningPathsPage'));
const EditLearningPathPage = React.lazy(() => import('./pages/learning/EditLearningPathPage'));
const LearningModuleView = React.lazy(() => import('./components/learning/LearningModuleView'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const PlatformSettingsPage = React.lazy(() => import('./pages/PlatformSettingsPage'));
const TemplateManagementPage = React.lazy(() => import('./pages/settings/TemplateManagementPage'));
const ActionPlanTemplateEditorPage = React.lazy(() => import('./pages/settings/ActionPlanTemplateEditorPage'));
const PublicAssessmentPage = React.lazy(() => import('./pages/PublicAssessmentPage'));
const PublicLinksManagementPage = React.lazy(() => import('./pages/PublicLinksManagementPage').then(m => ({ default: m.PublicLinksManagementPage })));
const AttendancePage = React.lazy(() => import('./pages/AttendancePage'));
const GamificationPage = React.lazy(() => import('./pages/GamificationPage'));
const MetricDetailPage = React.lazy(() => import('./pages/MetricDetailPage'));
const GoalDetailPage = React.lazy(() => import('./pages/GoalDetailPage'));
const AlertDetailPage = React.lazy(() => import('./pages/AlertDetailPage'));
const StrategicGoalDetailPage = React.lazy(() => import('./pages/StrategicGoalDetailPage'));
const PrioritiesPage = React.lazy(() => import('./pages/PrioritiesPage'));
const AlertReportPreview = React.lazy(() => import('./pages/AlertReportPreview'));
const CalendarPage = React.lazy(() => import('./pages/CalendarPageEnhanced'));
const ReservationsPage = React.lazy(() => import('./pages/ReservationsPage'));
const ActionPlansPage = React.lazy(() => import('./pages/ActionPlansPage'));
const ActionPlanDetailPage = React.lazy(() => import('./pages/ActionPlanDetailPage'));
const EditActionPlanPage = React.lazy(() => import('./pages/EditActionPlanPage'));

// Integrations pages
const IntegrationsPage = React.lazy(() => import('./pages/IntegrationsPage'));
const MemberPortalIntegrationPage = React.lazy(() => import('./pages/MemberPortalIntegrationPage'));
const OAuthCallbackPage = React.lazy(() => import('./pages/OAuthCallbackPage'));
const CornerstoneCallbackPage = React.lazy(() => import('./components/auth/CornerstoneCallbackPage'));
const PreflightErrorPage = React.lazy(() => import('./components/auth/PreflightErrorPage'));
const EventsAdminPage = React.lazy(() => import('./pages/EventsAdminPage'));

// Attendance components
const CheckInFlow = React.lazy(() => import('./components/attendance/CheckInFlow').then(m => ({ default: m.CheckInFlow })));
const AttendanceDetailPage = React.lazy(() => import('./pages/AttendanceDetailPage'));

// Metric Story pages
const EngagementStoryPage = React.lazy(() => import('./pages/metrics/EngagementStoryPage').then(m => ({ default: m.EngagementStoryPage })));
const GrowthStoryPage = React.lazy(() => import('./pages/metrics/GrowthStoryPage').then(m => ({ default: m.GrowthStoryPage })));
const GivingStoryPage = React.lazy(() => import('./pages/metrics/GivingStoryPage').then(m => ({ default: m.GivingStoryPage })));

// Detailed Giving pages
const GivingPatternsPage = React.lazy(() => import('./pages/giving/GivingPatternsPage').then(m => ({ default: m.GivingPatternsPage })));
const GivingPerCapitaPage = React.lazy(() => import('./pages/giving/GivingPerCapitaPage').then(m => ({ default: m.GivingPerCapitaPage })));
const WeeklyGivingPage = React.lazy(() => import('./pages/giving/WeeklyGivingPage').then(m => ({ default: m.WeeklyGivingPage })));

// Knowledge pages
const ResearchPage = React.lazy(() => import('./pages/knowledge/ResearchPage'));
const KnowledgeLearningPathsPage = React.lazy(() => import('./pages/knowledge/LearningPathsPage'));
const SubmitPracticePage = React.lazy(() => import('./pages/knowledge/SubmitPracticePage'));
const SubmitCaseStudyPage = React.lazy(() => import('./pages/knowledge/SubmitCaseStudyPage'));
const SubmitResearchPage = React.lazy(() => import('./pages/knowledge/SubmitResearchPage'));
const SearchResultsPage = React.lazy(() => import('./pages/knowledge/SearchResultsPage'));
const ModerationDashboard = React.lazy(() => import('./pages/knowledge/ModerationDashboard'));
const PracticeDetailPage = React.lazy(() => import('./pages/knowledge/PracticeDetailPage'));
const EditPracticePage = React.lazy(() => import('./pages/knowledge/EditPracticePage'));
const ResearchArticlePage = React.lazy(() => import('./pages/knowledge/ResearchArticlePage'));

// Learning Progress pages
const MyLearningDashboard = React.lazy(() => import('./pages/learning/MyLearningDashboard'));
const LearnPathPage = React.lazy(() => import('./pages/learning/LearnPathPage'));
const PathProgressDashboard = React.lazy(() => import('./pages/learning/PathProgressDashboard'));

// Prayer Team pages
const PrayerTeamDashboardPage = React.lazy(() => import('./pages/prayer/PrayerTeamDashboardPage'));
const PrayerRequestInboxPage = React.lazy(() => import('./pages/prayer/PrayerRequestInboxPage'));
const PrayerMinistrySettingsPage = React.lazy(() => import('./pages/prayer/PrayerMinistrySettingsPage'));
const PrayerAuditPage = React.lazy(() => import('./pages/admin/PrayerAuditPage'));

// Knowledge components (for legacy routes)
const PracticeLibrary = React.lazy(() => import('./components/knowledge').then(m => ({ default: m.PracticeLibrary })));

// Ministry pages
const CoverageGapsPage = React.lazy(() => import('./pages/CoverageGapsPage'));

// AI Agent components
const ActionHistory = React.lazy(() => import('./components/ai-agent').then(m => ({ default: m.ActionHistory })));

// Test components
const CurrencyTest = React.lazy(() => import('./pages/test/CurrencyTest').then(m => ({ default: m.CurrencyTest })));

// System/Admin pages
const PlatformHealthPage = React.lazy(() => import('./pages/PlatformHealthPage'));
const InboundSMSRoutesPage = React.lazy(() => import('./pages/settings/InboundSMSRoutesPage').then(m => ({ default: m.InboundSMSRoutesPage })));
const InboundSMSPage = React.lazy(() => import('./pages/messages/InboundSMSPage').then(m => ({ default: m.InboundSMSPage })));

// Platform Admin pages (SuperUser only)
const PlatformAdminLogin = React.lazy(() => import('./pages/platform-admin/PlatformAdminLogin'));
const PlatformAdminUsers = React.lazy(() => import('./pages/platform-admin/PlatformAdminUsers'));
const PlatformAdminSubscriptions = React.lazy(() => import('./pages/platform-admin/PlatformAdminSubscriptions'));
const PlatformAdminAddOns = React.lazy(() => import('./pages/platform-admin/PlatformAdminAddOns'));
const PlatformAdminTiers = React.lazy(() => import('./pages/platform-admin/PlatformAdminTiers'));
const PlatformAdminHealth = React.lazy(() => import('./pages/platform-admin/PlatformAdminHealth'));
const PlatformAdminLogs = React.lazy(() => import('./pages/platform-admin/PlatformAdminLogs'));
const PlatformAdminDatabase = React.lazy(() => import('./pages/platform-admin/PlatformAdminDatabase'));
const PlatformAdminCornerstone = React.lazy(() => import('./pages/platform-admin/PlatformAdminCornerstone'));

// Platform Admin components
const AdminLayout = React.lazy(() => import('./components/platform-admin/AdminLayout'));
const SuperUserRoute = React.lazy(() => import('./components/platform-admin/SuperUserRoute'));

// Member mobile app pages
const MemberPINSetupPage = React.lazy(() => import('./pages/member/MemberPINSetupPage'));
const MemberPINResetPage = React.lazy(() => import('./pages/member/MemberPINResetPage'));
const MemberKioskPage = React.lazy(() => import('./pages/member/MemberKioskPage'));
const MemberPortalPage = React.lazy(() => import('./pages/member/MemberPortalPage'));

// PWA Member pages (separate from responsive design)
const PWAMemberPortalPage = React.lazy(() => import('./pages/member/pwa/PWAMemberPortalPage'));
const PWAMemberProfilePage = React.lazy(() => import('./pages/member/pwa/PWAMemberProfilePage'));
const PWAGamificationPage = React.lazy(() => import('./pages/member/pwa/PWAGamificationPage'));
const PWAAssessmentPage = React.lazy(() => import('./pages/member/pwa/PWAAssessmentPage'));
const PWAGroupsPage = React.lazy(() => import('./pages/member/pwa/PWAGroupsPage'));
const PWAMinistriesPage = React.lazy(() => import('./pages/member/pwa/PWAMinistriesPage'));
const PWAAssessmentsListPage = React.lazy(() => import('./pages/member/pwa/PWAAssessmentsListPage'));
const PWAPollsPage = React.lazy(() => import('./pages/member/pwa/PWAPollsPage'));
const PWAMorePage = React.lazy(() => import('./pages/member/pwa/PWAMorePage'));
const PWAForumPage = React.lazy(() => import('./pages/member/pwa/PWAForumPage'));

// Auth components
import LoginFixedWrapper from './components/auth/LoginFixedWrapper';
import RegisterWrapper from './components/auth/RegisterWrapper';
import ForgotPasswordWrapper from './components/auth/ForgotPasswordWrapper';
import ResetPasswordWrapper from './components/auth/ResetPasswordWrapper';

// Layout components
import MainLayoutWrapper from './components/layout/MainLayoutWrapper';
import AuthLayoutFixedWrapper from './components/layout/AuthLayoutFixedWrapper';

// Simple loading fallback - use general context since we can't access location outside Router
const LoadingFallback: React.FC = () => {
  return <ScriptureLoader context="general" />;
};

// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Debug logging
  if (location.pathname === '/dashboard') {
    console.log('[ProtectedRoute] Dashboard check:', {
      isAuthenticated,
      isLoading,
      hasUser: !!user,
      userEmail: user?.email,
      sessionActive: sessionStorage.getItem('connectpath_active_session')
    });
  }

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    // Pass the current location to login page so it can redirect back
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// Wrapper for CheckInFlow in kiosk mode to pass churchId
const CheckInFlowWrapper: React.FC = () => {
  const { user } = useAuth();
  return <CheckInFlow isKioskMode={true} churchId={user?.churchId || ''} />;
};

const Router: React.FC = () => {
  return (
    <BrowserRouter>
      <AIAgentProvider>
        <RouterListener />
        <ErrorBoundary>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
                {/* Public routes */}
                <Route path="/" element={<HomePage />} />
            {/* Test/diagnostic routes removed from production — use dev server only */}
            
            {/* Public assessment route */}
            <Route path="/assessment/public/:slug" element={<PublicAssessmentPage />} />
            
            {/* Auth routes */}
            <Route path="/login" element={<AuthLayoutFixedWrapper><LoginFixedWrapper /></AuthLayoutFixedWrapper>} />
            <Route path="/auth/cornerstone/callback" element={<CornerstoneCallbackPage />} />
            <Route path="/auth/cornerstone/preflight-error" element={<PreflightErrorPage />} />
            <Route path="/register" element={<AuthLayoutFixedWrapper><RegisterWrapper /></AuthLayoutFixedWrapper>} />
            <Route path="/forgot-password" element={<AuthLayoutFixedWrapper><ForgotPasswordWrapper /></AuthLayoutFixedWrapper>} />
            <Route path="/reset-password/:uid/:token" element={<AuthLayoutFixedWrapper><ResetPasswordWrapper /></AuthLayoutFixedWrapper>} />
            
            {/* Protected routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <DashboardPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            {/* /test/currency removed from production */}
            
            <Route path="/dashboards/executive" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <ExecutiveDashboardPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/dashboards/ministry" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <MinistryDashboardPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/dashboards/pastoral-care" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <PastoralCareDashboardPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            
            {/* Add alternative dashboard routes that were tested - these don't require auth based on audit */}
            <Route path="/dashboard/executive" element={
              <MainLayoutWrapper>
                <ExecutiveDashboardPage />
              </MainLayoutWrapper>
            } />
            
            <Route path="/dashboard/ministry" element={
              <MainLayoutWrapper>
                <MinistryDashboardPage />
              </MainLayoutWrapper>
            } />
            
            <Route path="/dashboard/pastoral" element={
              <MainLayoutWrapper>
                <PastoralCareDashboardPage />
              </MainLayoutWrapper>
            } />

            <Route path="/impact" element={
              <MainLayoutWrapper>
                <ImpactDashboardPage />
              </MainLayoutWrapper>
            } />

            <Route path="/assessments" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <AssessmentListPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/assessments/create" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <AssessmentCreatePageNew />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/assessments/:id" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <AssessmentDetailPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/assessments/edit/:id" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <AssessmentEditPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/assessments/:id/respond" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <AssessmentResponsePage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/assessments/:id/analysis" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <AssessmentAnalysisPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/assessments/public-links" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <PublicLinksManagementPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/survey/:assessmentId" element={
              <ProtectedRoute>
                <SurveyPage />
              </ProtectedRoute>
            } />
            
            {/* Add general survey route without assessmentId - doesn't require auth based on audit */}
            <Route path="/survey" element={
              <MainLayoutWrapper>
                <SurveyPage />
              </MainLayoutWrapper>
            } />
            
            <Route path="/templates" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <AssessmentTemplateLibraryPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/templates/new" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <AssessmentTemplatePage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/templates/:id" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <AssessmentTemplatePage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/analytics" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <AnalyticsPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/priorities" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <PrioritiesPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/analytics/:tab" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <AnalyticsPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/analytics/dashboard/:churchId" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <AnalyticsDashboardPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/analytics/dashboard" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <AnalyticsDashboardPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            
            
            <Route path="/analytics/dashboards/:dashboardId/visualizations/create" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <VisualizationCreatePage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/analytics/dashboards/:dashboardId/visualizations/:visualizationId/edit" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <VisualizationCreatePage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/reporting/schedule/:reportId" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <ReportSchedulingPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/reporting" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <ReportingPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            {/* Strategic Planning Routes */}
            <Route path="/strategic-planning" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <StrategicPlanningPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/strategic-planning/:tab" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <StrategicPlanningPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/strategic-planning/goals/:id" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <StrategicGoalDetailPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            {/* Learning Paths Routes */}
            <Route path="/learning" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <LearningPathsMainPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            {/* Alert, Metric and Goal Detail Routes */}
            <Route path="/alerts/:alertId" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <AlertDetailPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/alert-report-preview" element={
              <ProtectedRoute>
                <AlertReportPreview />
              </ProtectedRoute>
            } />
            
            <Route path="/metrics/:id" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <MetricDetailPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/goals/:id" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <GoalDetailPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            {/* Add missing report routes - these don't require auth based on audit */}
            <Route path="/reports" element={
              <MainLayoutWrapper>
                <ReportingPage />
              </MainLayoutWrapper>
            } />
            
            <Route path="/reports/scheduling" element={
              <MainLayoutWrapper>
                <ReportSchedulingPage />
              </MainLayoutWrapper>
            } />
            
            <Route path="/profile" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <ChurchProfilePage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            {/* Add /church route - doesn't require auth based on audit */}
            <Route path="/church" element={
              <MainLayoutWrapper>
                <ChurchProfilePage />
              </MainLayoutWrapper>
            } />
            
            {/* Add church edit route */}
            <Route path="/church/:churchId/edit" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <ChurchProfilePage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/user-profile" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <UserProfilePage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            <Route path="/theme/palette-builder" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <ThemePaletteBuilderPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            <Route path="/attendance" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <AttendancePage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/attendance/detail" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <AttendanceDetailPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            {/* Metric Story Routes */}
            <Route path="/metrics/attendance" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <AttendanceDetailPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/metrics/engagement" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <EngagementStoryPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/metrics/growth" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <GrowthStoryPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/metrics/giving" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <GivingStoryPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            {/* Main Giving Route - Redirect to metrics/giving */}
            <Route path="/giving" element={<Navigate to="/metrics/giving" replace />} />

            {/* Detailed Giving Pages */}
            <Route path="/giving/patterns" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <GivingPatternsPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            <Route path="/giving/per-capita" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <GivingPerCapitaPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            <Route path="/giving/weekly" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <WeeklyGivingPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            <Route path="/calendar" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <CalendarPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            <Route path="/admin/events" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <EventsAdminPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            <Route path="/reservations" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <ReservationsPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            <Route path="/engagement" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <MemberEngagementPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/action-plans" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <ActionPlansPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/action-plans/:id" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <ActionPlanDetailPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            <Route path="/action-plans/:id/edit" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <EditActionPlanPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            <Route path="/gamification" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <GamificationPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            {/* Member Management routes - redirect to church profile */}
            <Route path="/members" element={
              <ProtectedRoute>
                <Navigate to="/profile?tab=members" replace />
              </ProtectedRoute>
            } />

            {/* Create new member - must be before :memberId route */}
            <Route path="/members/new" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <MemberCreatePage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            <Route path="/members/:memberId" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <MemberProfile />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            {/* Ministry Management routes */}
            <Route path="/church/:churchId/ministries" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <MinistryDashboardPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/church/:churchId/ministries/:ministryId" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <MinistryDetailPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            {/* Add specific ministry routes first to prevent them being caught by :id param */}
            <Route path="/ministry/coverage" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <CoverageGapsPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            <Route path="/ministry/effectiveness" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <MinistryDashboardPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            <Route path="/ministry/participation" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <MinistryDashboardPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            {/* Generic ministry detail route - must come AFTER specific routes */}
            <Route path="/ministry/:id" element={
              <MainLayoutWrapper>
                <MinistryDetailPage />
              </MainLayoutWrapper>
            } />
            
            {/* Knowledge Base routes */}
            <Route path="/knowledge" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <KnowledgeBasePage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/knowledge/practices" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <PracticeLibrary />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/knowledge/practices/:id" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <PracticeDetailPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            <Route path="/knowledge/practices/:id/edit" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <EditPracticePage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            <Route path="/knowledge/practices/:id/implement" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <PracticeLibrary />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/knowledge/practices/submit" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <SubmitPracticePage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/knowledge/case-studies/submit" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <SubmitCaseStudyPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/knowledge/case-studies/:id" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <PracticeDetailPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            {/* Learning Paths routes - moved to main planning hierarchy */}
            <Route path="/learning-paths" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <LearningPathsMainPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            <Route path="/learning-paths/:pathId" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <LearningModuleView />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            <Route path="/learning-paths/:id/edit" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <EditLearningPathPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            {/* New Learning Progress routes */}
            <Route path="/my-learning" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <MyLearningDashboard />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            <Route path="/learning-paths/:id/learn" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <LearnPathPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            <Route path="/learning-paths/:id/progress" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <PathProgressDashboard />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            {/* Legacy routes for backward compatibility */}
            <Route path="/knowledge/learning-paths" element={
              <Navigate to="/learning-paths" replace />
            } />
            
            <Route path="/knowledge/learning-paths/:pathId" element={
              <Navigate to="/learning-paths" replace />
            } />
            
            <Route path="/knowledge/research" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <ResearchPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            {/* Submit route must come BEFORE the parametric :id route */}
            <Route path="/knowledge/research/submit" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <SubmitResearchPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            <Route path="/knowledge/research/:id" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <ResearchArticlePage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            {/* Knowledge Search and Moderation */}
            <Route path="/knowledge/search" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <SearchResultsPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            <Route path="/knowledge/moderate" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <ModerationDashboard />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />
            
            {/* AI Agent History route */}
            <Route path="/ai-agent/history" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <ActionHistory />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            {/* Prayer Team Dashboard route */}
            <Route path="/prayer-team" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <PrayerTeamDashboardPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            {/* Prayer Request Inbox routes (email-client style) */}
            <Route path="/prayer-requests" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <PrayerRequestInboxPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            <Route path="/prayer-requests/settings" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <PrayerMinistrySettingsPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            {/* Prayer Audit - Senior Leadership QA & Investigation */}
            <Route path="/admin/prayer-audit" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <PrayerAuditPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            {/* Integrations routes - admin only */}
            <Route path="/integrations" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <IntegrationsPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            <Route path="/integrations/member-portal" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <MemberPortalIntegrationPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            {/* Settings route - admin only */}
            <Route path="/settings" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <SettingsPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            {/* Template Management - admin only */}
            <Route path="/settings/templates" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <TemplateManagementPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            {/* Action Plan Template Editor - admin only */}
            <Route path="/settings/templates/action-plans/:id/edit" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <ActionPlanTemplateEditorPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            {/* Calendar OAuth callback routes */}
            <Route path="/settings/integrations/calendar/:provider/callback" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <OAuthCallbackPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            {/* Platform Settings route - SuperUser only */}
            <Route path="/platform-settings" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <PlatformSettingsPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            {/* SMS Settings routes */}
            <Route path="/settings/inbound-sms-routes" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <InboundSMSRoutesPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            <Route path="/messages/inbound-sms" element={
              <ProtectedRoute>
                <MainLayoutWrapper>
                  <InboundSMSPage />
                </MainLayoutWrapper>
              </ProtectedRoute>
            } />

            {/* Platform Health Monitoring - public for monitoring systems */}
            <Route path="/health" element={<PlatformHealthPage />} />
            <Route path="/platform-health" element={<PlatformHealthPage />} />
            
            {/* Member Mobile App routes (public for PIN setup/reset) */}
            <Route path="/member/pin/setup" element={<MemberPINSetupPage />} />
            <Route path="/member/pin/reset" element={<MemberPINResetPage />} />
            <Route path="/member/portal" element={<MemberPortalPage />} />
            <Route path="/member/kiosk" element={
              <ProtectedRoute>
                <MemberKioskPage />
              </ProtectedRoute>
            } />

            {/* PWA Member routes (professional mobile app interface) */}
            <Route path="/member/pwa/portal" element={<PWAMemberPortalPage />} />
            <Route path="/member/pwa" element={<PWAMemberPortalPage />} />
            <Route path="/member/pwa/profile" element={<PWAMemberProfilePage />} />
            <Route path="/member/pwa/gamification" element={<PWAGamificationPage />} />
            <Route path="/member/pwa/assessment" element={<PWAAssessmentPage />} />
            <Route path="/member/pwa/groups" element={<PWAGroupsPage />} />
            <Route path="/member/pwa/ministries" element={<PWAMinistriesPage />} />
            <Route path="/member/pwa/assessments" element={<PWAAssessmentsListPage />} />
            <Route path="/member/pwa/polls" element={<PWAPollsPage />} />
            <Route path="/member/pwa/more" element={<PWAMorePage />} />
            <Route path="/member/pwa/forum" element={<PWAForumPage />} />

            {/* Kiosk routes */}
            <Route path="/kiosk/login" element={<LoginFixedWrapper isKioskMode={true} />} />
            <Route path="/kiosk/checkin" element={
              <ProtectedRoute>
                <CheckInFlowWrapper />
              </ProtectedRoute>
            } />

            {/* Platform Admin routes (SuperUser only) - Separate auth context */}
            <Route path="/platform-admin/login" element={
              <PlatformAdminAuthProvider>
                <PlatformAdminLogin />
              </PlatformAdminAuthProvider>
            } />
            <Route path="/platform-admin" element={
              <PlatformAdminAuthProvider>
                <SuperUserRoute>
                  <AdminLayout>
                    <Navigate to="/platform-admin/users" replace />
                  </AdminLayout>
                </SuperUserRoute>
              </PlatformAdminAuthProvider>
            } />
            <Route path="/platform-admin/users" element={
              <PlatformAdminAuthProvider>
                <SuperUserRoute>
                  <AdminLayout>
                    <PlatformAdminUsers />
                  </AdminLayout>
                </SuperUserRoute>
              </PlatformAdminAuthProvider>
            } />
            <Route path="/platform-admin/subscriptions" element={
              <PlatformAdminAuthProvider>
                <SuperUserRoute>
                  <AdminLayout>
                    <PlatformAdminSubscriptions />
                  </AdminLayout>
                </SuperUserRoute>
              </PlatformAdminAuthProvider>
            } />
            <Route path="/platform-admin/add-ons" element={
              <PlatformAdminAuthProvider>
                <SuperUserRoute>
                  <AdminLayout>
                    <PlatformAdminAddOns />
                  </AdminLayout>
                </SuperUserRoute>
              </PlatformAdminAuthProvider>
            } />
            <Route path="/platform-admin/tiers" element={
              <PlatformAdminAuthProvider>
                <SuperUserRoute>
                  <AdminLayout>
                    <PlatformAdminTiers />
                  </AdminLayout>
                </SuperUserRoute>
              </PlatformAdminAuthProvider>
            } />
            <Route path="/platform-admin/health" element={
              <PlatformAdminAuthProvider>
                <SuperUserRoute>
                  <AdminLayout>
                    <PlatformAdminHealth />
                  </AdminLayout>
                </SuperUserRoute>
              </PlatformAdminAuthProvider>
            } />
            <Route path="/platform-admin/logs" element={
              <PlatformAdminAuthProvider>
                <SuperUserRoute>
                  <AdminLayout>
                    <PlatformAdminLogs />
                  </AdminLayout>
                </SuperUserRoute>
              </PlatformAdminAuthProvider>
            } />
            <Route path="/platform-admin/database" element={
              <PlatformAdminAuthProvider>
                <SuperUserRoute>
                  <AdminLayout>
                    <PlatformAdminDatabase />
                  </AdminLayout>
                </SuperUserRoute>
              </PlatformAdminAuthProvider>
            } />
            <Route path="/platform-admin/cornerstone" element={
              <PlatformAdminAuthProvider>
                <SuperUserRoute>
                  <AdminLayout>
                    <PlatformAdminCornerstone />
                  </AdminLayout>
                </SuperUserRoute>
              </PlatformAdminAuthProvider>
            } />

            {/* 404 route */}
            <Route path="*" element={<NotFoundPageWrapper />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
        {/* Global subscription upgrade modal - listens for 402 errors */}
        <UpgradeModal />
      </AIAgentProvider>
    </BrowserRouter>
  );
};

export default Router;